import type {
  Session,
  DisconnectRequest,
  LogOffRequest,
  SessionStatusResponse,
  ApiError,
  AdminSessionActionRequest,
  AdminSessionMessageRequest,
  BulkSessionActionRequest,
  BulkActionResponse
} from '@/types/api';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig, apiConfig } from '@/config/authConfig';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://localhost:7094/api';

class ApiClient {
  private msalInstance: PublicClientApplication;

  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
  }

  private async getAuthToken(): Promise<string> {
    try {
      await this.msalInstance.initialize();
      
      const account = this.msalInstance.getActiveAccount();
      if (!account) {
        throw new Error('No active account found. Please sign in.');
      }

      const silentRequest = {
        scopes: apiConfig.scopes,
        account: account,
      };

      try {
        const response = await this.msalInstance.acquireTokenSilent(silentRequest);
        return `Bearer ${response.accessToken}`;
      } catch (silentError) {
        if (silentError instanceof InteractionRequiredAuthError) {
          const response = await this.msalInstance.acquireTokenPopup(silentRequest);
          return `Bearer ${response.accessToken}`;
        }
        throw silentError;
      }
    } catch (error) {
      console.error('Failed to acquire token:', error);
      throw new Error('Authentication failed. Please sign in again.');
    }
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
        ...options.headers,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Handle unauthorized - trigger re-authentication
        throw new Error('Unauthorized. Please sign in again.');
      }

      if (response.status === 403) {
        // Handle forbidden - user lacks required permissions/roles
        throw new Error('Access denied. You do not have permission to perform this action. Please contact your administrator if you believe you should have access.');
      }

      // Try to parse error response
      try {
        const error = await response.json() as ApiError;
        throw new Error(error.message || error.error || `Request failed with status ${response.status}`);
      } catch (parseError) {
        // If JSON parsing fails, throw a generic error with status code
        if (parseError instanceof Error && parseError.message.includes('Access denied')) {
          throw parseError; // Re-throw our 403 error
        }
        throw new Error(`Request failed with status ${response.status}`);
      }
    }

    return response;
  }

  async getSessions(): Promise<Session[]> {
    const response = await this.fetchWithAuth(`${API_BASE_URL}/sessions`);
    return response.json();
  }

  async refreshSessions(): Promise<Session[]> {
    const response = await this.fetchWithAuth(`${API_BASE_URL}/sessions/refresh`, {
      method: 'POST',
    });
    return response.json();
  }

  async getSession(sessionId: string): Promise<Session> {
    const response = await this.fetchWithAuth(`${API_BASE_URL}/sessions/${sessionId}`);
    return response.json();
  }

  async disconnectSession(sessionId: string, request: DisconnectRequest): Promise<void> {
    await this.fetchWithAuth(`${API_BASE_URL}/sessions/${sessionId}/disconnect`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async logOffSession(sessionId: string, request: LogOffRequest): Promise<void> {
    await this.fetchWithAuth(`${API_BASE_URL}/sessions/${sessionId}/logoff`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getSessionStatus(sessionId: string): Promise<SessionStatusResponse> {
    const response = await this.fetchWithAuth(`${API_BASE_URL}/sessions/${sessionId}/status`);
    return response.json();
  }

  // Admin methods
  async getAllSessions(): Promise<Session[]> {
    const response = await this.fetchWithAuth(`${API_BASE_URL}/admin/sessions`);
    return response.json();
  }

  async adminDisconnectSession(sessionId: string, request: AdminSessionActionRequest): Promise<void> {
    await this.fetchWithAuth(`${API_BASE_URL}/admin/sessions/${sessionId}/disconnect`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async adminLogOffSession(sessionId: string, request: AdminSessionActionRequest): Promise<void> {
    await this.fetchWithAuth(`${API_BASE_URL}/admin/sessions/${sessionId}/logoff`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async adminSendMessageToSession(sessionId: string, request: AdminSessionMessageRequest): Promise<void> {
    await this.fetchWithAuth(`${API_BASE_URL}/admin/sessions/${sessionId}/message`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async bulkSessionAction(request: BulkSessionActionRequest): Promise<BulkActionResponse> {
    const response = await this.fetchWithAuth(`${API_BASE_URL}/admin/sessions/bulk`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return response.json();
  }

  async forceHealthCheck(): Promise<void> {
    await this.fetchWithAuth(`${API_BASE_URL}/admin/sessions/health-check`, {
      method: 'POST',
    });
  }

  async forceRefresh(): Promise<void> {
    await this.fetchWithAuth(`${API_BASE_URL}/admin/sessions/refresh`, {
      method: 'POST',
    });
  }
}

export const apiClient = new ApiClient();